import { c as _c } from "react/compiler-runtime";
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Sheet, SheetActions, SheetBody, SheetClose, SheetContent, SheetFooter, SheetHeader, SheetTrigger } from '@sola/ark-core/molecules/sheet/sheet.tsx';
import { ChildImageVariantEnum } from '@sola/ark-core/utils/child-image.ts';
import { ChildBioTabs } from '@sola/ark-skippy/molecules/child-bio-details/child-bio-tabs.tsx';
import { ChildBioImage } from '@sola/ark-skippy/molecules/child-bio-image/child-bio-image.tsx';
import { useChildDetails } from '@sola/my-sdk/children/hooks.ts';
import type { ChildAvailabilityItem, ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import useModalAlerts from '../../molecules/local-alerts/useModalAlerts.ts';
export interface SubChildBioSheetProps {
  availability: ChildAvailabilityItem;
  childDetails?: ChildDetails;
  open?: boolean;
  onAccept?: (childGlobalId: string) => void | Promise<void>;
  onOpenChange?: (open: boolean) => void;
  openTrigger?: ReactElement;
}
export interface SubChildBioProps {
  childDetails: ChildDetails;
  availability: ChildAvailabilityItem;
}
const SubChildBio: React.FC<SubChildBioProps> = t0 => {
  const $ = _c(8);
  const {
    childDetails,
    availability
  } = t0;
  let t1;
  if ($[0] !== availability || $[1] !== childDetails) {
    t1 = <ChildBioImage details={childDetails} availability={availability} aspectRatio="16-9" childImageVariant={ChildImageVariantEnum.Landscape} showCaption={false} sizeIntent="full" />;
    $[0] = availability;
    $[1] = childDetails;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== childDetails) {
    t2 = <ChildBioTabs details={childDetails} />;
    $[3] = childDetails;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className="flex flex-col body-md gap-comfy-xl label-neutral-mild">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
export const DepartureSubChildBioSheet: React.FC<SubChildBioSheetProps> = ({
  availability,
  childDetails: providedChildDetails,
  open,
  onAccept,
  onOpenChange,
  openTrigger
}) => {
  const {
    t
  } = useTranslation(['my']);
  const [internalOpen, setInternalOpen] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [hasAcceptError, setHasAcceptError] = useState(false);
  const {
    handleOpenChange
  } = useModalAlerts();
  const isOpen = open ?? internalOpen;
  const childDetailsQuery = useChildDetails(isOpen && !providedChildDetails ? availability.globalId : undefined);
  const childDetails = providedChildDetails ?? childDetailsQuery.data;
  const setOpen = (nextOpen: boolean) => {
    if (!nextOpen) {
      setHasAcceptError(false);
    }
    handleOpenChange(nextOpen, onOpenChange ?? setInternalOpen);
  };
  const handleAccept = async () => {
    if (!onAccept || isAccepting) {
      return;
    }
    setHasAcceptError(false);
    setIsAccepting(true);
    try {
      await Promise.resolve(onAccept(availability.globalId));
      setOpen(false);
    } catch {
      setHasAcceptError(true);
    } finally {
      setIsAccepting(false);
    }
  };
  const childName = childDetails?.name.preferred || childDetails?.name.first || availability.name.preferred || availability.name.first;
  const title = t('my:sponsorshipRelationship.departures.subChildBioSheet.heading', {
    childName
  });
  return <Sheet open={isOpen} onOpenChange={setOpen} isBusy={isAccepting}>
      {openTrigger && <SheetTrigger>{openTrigger}</SheetTrigger>}
      <SheetContent title={title}>
        <SheetHeader>{title}</SheetHeader>
        <SheetBody>
          {(childDetailsQuery.isLoading || childDetailsQuery.isPending) && !childDetails && <div className="flex flex-col gap-comfy-lg" aria-busy>
                <Skeleton height={32} width={240} />
                <Skeleton height={360} width="100%" borderRadius="1rem" />
                <Skeleton height={18} width="100%" />
                <Skeleton height={18} width="80%" />
              </div>}

          {childDetailsQuery.isError && !childDetails && <Alert variant="negative" title={t('my:sponsorshipRelationship.departures.subChildBioSheet.error.title')} message={t('my:sponsorshipRelationship.departures.subChildBioSheet.error.message')} />}

          {hasAcceptError && <Alert variant="negative" title={t('my:sponsorshipRelationship.departures.subChildBioSheet.acceptError.title')} message={t('my:sponsorshipRelationship.departures.subChildBioSheet.acceptError.message')} />}

          {childDetails && <SubChildBio childDetails={childDetails} availability={availability} />}
        </SheetBody>

        {childDetails && <SheetFooter>
            <SheetActions>
              <SheetClose asChild>
                <ButtonStyle size="lg" purpose="neutral" variant="secondary" fullWidth>
                  <button type="button">
                    {t('my:sponsorshipRelationship.departures.subChildBioSheet.footer.buttonClose')}
                  </button>
                </ButtonStyle>
              </SheetClose>

              <ButtonStyle size="lg" variant="primary" fullWidth disabled={!onAccept} isBusy={isAccepting}>
                <button type="button" onClick={() => void handleAccept()}>
                  {t('my:sponsorshipRelationship.departures.subChildBioSheet.footer.buttonAccept', {
                childName
              })}
                </button>
              </ButtonStyle>
            </SheetActions>
          </SheetFooter>}
      </SheetContent>
    </Sheet>;
};